import {
  DialogQueue,
  createDialogQueue,
  createSnackbarQueue,
  SnackbarQueue,
  type DialogPromise,
  type PromptBodyProps,
  type SnackbarProps,
} from "@mg/dali/src";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ProviderProps,
  useRef,
} from "react";
import { createPortal } from "react-dom";

import { consumePendingNotifications } from "../utils/pendingNotifications";

/**
 * Test id for the snackbar host wrapper. Exposed so unit tests can pin
 * the portal target and the z-index token without scraping the DOM.
 */
export const SNACKBAR_HOST_TEST_ID = "snackbar-host";

/**
 * Tailwind z-index token applied to the portaled snackbar host.
 *
 * The comments drawer (`frontends/carrie/src/components/sidebar/Sidebar.tsx`)
 * renders inside `fixed inset-y-0 z-20`. Before this fix, the snackbar
 * host shared the parent stacking context as the drawer (and dali's
 * default `dali-z-20`), so the right-hand drawer column visually clipped
 * any toast that fired on the ticket route.
 *
 * Putting the snackbar host on a new stacking context one tier above
 * `z-20` keeps it visible over the drawer while still sitting BELOW the
 * dali native `<dialog>` modals (which use the browser top-layer via
 * `showModal()` and so are unaffected by this z-index) and below the
 * heavy app-wide overlays like `VersionUpdateBanner` (z-[2000]) and
 * `HighlightModal` (z-[201]).
 *
 * Using exact-match string here so the unit test in
 * `frontends/carrie/src/contexts/__tests__/ui.test.tsx` can assert on
 * the exact token and fail loudly if anyone drifts it.
 */
export const SNACKBAR_HOST_Z_INDEX_CLASS = "z-[60]";

interface UIContextValues {
  alert: (dialog: Partial<DialogPromise>) => Promise<boolean | null>;
  confirm: (dialog: Partial<DialogPromise>) => Promise<boolean | null>;
  prompt: (
    dialog: Partial<DialogPromise> & Partial<PromptBodyProps>,
  ) => Promise<string | null>;
  notify: (message: SnackbarProps) => void;
  isMobile: boolean;
  rootRef: React.RefObject<HTMLElement>;
}

export const UIContext = createContext({} as UIContextValues);

export function UIProvider(
  props: JSX.ElementChildrenAttribute &
    Omit<ProviderProps<Partial<UIContextValues>>, "value">,
) {
  const { children, ...rest } = props;
  const { dialogs, alert, confirm, prompt } = createDialogQueue();
  const { messages, notify } = createSnackbarQueue();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const rootRef = useRef(null);

  useEffect(() => {
    function handleWindowResize() {
      if (window.innerWidth < 600) {
        return setIsMobile(true);
      }

      return setIsMobile(false);
    }

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  // Drain any notifications that were stashed via
  // `stashPendingNotification(...)` immediately before a hard reload
  // (CreateEnterpriseDialog, EnterpriseSwitcher, the Vitalora demo
  // reset, etc.). Without this, the user's confirmation toast for
  // those flows lives for ~1 second and then gets killed when the
  // reload tears down the React tree — perceptibly equivalent to "no
  // toast at all", which is what the 2026-05-12 nightly failures were
  // surfacing for enterprise creation. See
  // `frontends/carrie/src/utils/pendingNotifications.ts` for the full
  // rationale and contract.
  //
  // Runs once on mount: `consumePendingNotifications` clears the
  // sessionStorage slot on read, so a remount of this provider
  // without an intervening stash is a no-op. Empty-array fast path
  // is also a no-op so this is cheap when the queue is empty (the
  // common case for the vast majority of mounts).
  useEffect(() => {
    const pending = consumePendingNotifications();
    if (pending.length === 0) return;
    for (const payload of pending) {
      notify(payload);
    }
    // `notify` is a stable reference produced by `createSnackbarQueue()`
    // outside of state, so depending on it here would over-trigger on
    // every render. We intentionally run only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uiValue = useMemo(
    () => ({
      dialogs,
      alert,
      confirm,
      prompt,
      notify,
      messages,
      isMobile,
      rootRef,
    }),
    [dialogs, alert, confirm, prompt, notify, messages, isMobile, rootRef],
  );

  // Portal the snackbar host directly to <body> so it escapes any
  // ancestor that traps it (overflow / transform / stacking context).
  // Concretely: the share-link ticket view mounts a right-hand
  // comments drawer at `fixed ... z-20`, and previously the snackbar
  // host sat inside the same root tree at dali's default `z-20` and
  // got clipped by the drawer column ("Check your inbox" toast bug,
  // see commit b25b984ed). Portaling + a higher z-index hoists the
  // toast above the drawer regardless of route.
  const snackbarHost =
    typeof document === "undefined"
      ? null
      : createPortal(
          <div
            className={`relative ${SNACKBAR_HOST_Z_INDEX_CLASS}`}
            data-testid={SNACKBAR_HOST_TEST_ID}
          >
            <SnackbarQueue messages={messages} />
          </div>,
          document.body,
        );

  return (
    // @ts-expect-error TS2322: the `prompt` dialog needs its type updated in
    // Dali
    <UIContext.Provider value={uiValue} {...rest}>
      <DialogQueue dialogs={dialogs} />
      {snackbarHost}
      {children}
    </UIContext.Provider>
  );
}

export const useUI = () => useContext(UIContext);
